/**
 * SettingsPage (설정 메인 페이지)
 *
 * 앱의 모든 설정 메뉴를 보여주는 메인 설정 화면
 *
 * 책임:
 * - 설정 메뉴 목록 표시
 * - 각 설정 페이지로 네비게이션
 */

import { Link, useNavigate } from 'react-router-dom'
import { isDevelopment } from '@/core/config/env'
import { useAuth } from '@/core/auth/useAuth'

export function SettingsPage() {
  const { logout } = useAuth()
  const navigate = useNavigate()

  /**
   * 로그아웃 처리 및 첫 화면으로 리다이렉트
   *
   * @why Header에서 이동한 로직 (동일한 동작 유지)
   * - 사용자 인증 상태를 클리어하고 루트 경로(/)로 이동
   * - replace: true로 뒤로가기 시 인증 페이지 복귀 방지
   */
  const handleLogout = () => {
    logout()
    navigate('/', { replace: true })
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="px-4 py-6 max-w-4xl mx-auto">
        {/* 페이지 헤더: 제목 + 로그아웃 버튼 */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">설정</h1>
          <button
            onClick={handleLogout}
            className="px-4 py-2.5 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            aria-label="로그아웃"
          >
            로그아웃
          </button>
        </div>

        {/* 설정 메뉴 리스트 */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm divide-y divide-gray-200 dark:divide-gray-700">
          {/* 예산 설정 */}
          <Link
            to="/settings/budget"
            className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">💰</span>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">예산 설정</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">카테고리별 월 예산 설정</p>
              </div>
            </div>
            <span className="text-gray-400 dark:text-gray-500">→</span>
          </Link>

          {/* 결제수단 관리 */}
          <Link
            to="/settings/payment-methods"
            className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">💳</span>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">결제수단 관리</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">카드, 현금 등 결제수단 추가/삭제</p>
              </div>
            </div>
            <span className="text-gray-400 dark:text-gray-500">→</span>
          </Link>

          {/* 카테고리 관리 */}
          <Link
            to="/settings/categories"
            className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">📁</span>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">카테고리 관리</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">수입/지출 카테고리 추가/삭제</p>
              </div>
            </div>
            <span className="text-gray-400 dark:text-gray-500">→</span>
          </Link>

          {/* 멤버 관리 */}
          <Link
            to="/settings/members"
            className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">👥</span>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">멤버 관리</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">가계부 멤버 초대/역할 관리</p>
              </div>
            </div>
            <span className="text-gray-400 dark:text-gray-500">→</span>
          </Link>

          {/* 가계부 관리 */}
          <Link
            to="/settings/workspaces"
            className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">📂</span>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">가계부 관리</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">여러 가계부 추가/전환/관리</p>
              </div>
            </div>
            <span className="text-gray-400 dark:text-gray-500">→</span>
          </Link>

          {/* 앱 설정 */}
          <Link
            to="/settings/app"
            className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚙️</span>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">앱 설정</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">다크모드, 테마 등</p>
              </div>
            </div>
            <span className="text-gray-400 dark:text-gray-500">→</span>
          </Link>

          {/* 개발자 설정 (개발 모드에서만 표시) */}
          {isDevelopment() && (
            <Link
              to="/settings/developer"
              className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">🔧</span>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">개발자 설정</h3>
                    <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-200 text-xs font-medium rounded">
                      개발 전용
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Mock 모드, 스프레드시트 초기화</p>
                </div>
              </div>
              <span className="text-gray-400 dark:text-gray-500">→</span>
            </Link>
          )}
        </div>

        {/* 안내 메시지 */}
        <div className="mt-6 bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
          <p className="text-sm text-primary-800 dark:text-primary-200">
            💡 설정한 예산은 매달 자동으로 적용됩니다
          </p>
        </div>
      </div>
    </div>
  )
}
